import type { ShopperAiTagGroupId } from "@redotech/effect-api-shared/ids/definitions";
import type { TagResponse } from "@redotech/effect-api-shared/rpc/tag";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import { toast } from "@redotech/redo-web/alert";
import { useMutation } from "@tanstack/react-query";
import { Effect } from "effect";
import { catchApiErrors } from "../db/error-handling";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";

export interface CreateSupportTicketTagInput {
  tagGroupId: ShopperAiTagGroupId;
  name: string;
  pillTheme: PillTheme | null;
}

/**
 * Creates a new account tag inside an existing tag group via the Effect API and
 * refreshes the shared tag collection so every picker (per-ticket sidebar, bulk
 * menu, actions menu) sees the new tag immediately — without a page refresh.
 *
 * We refetch the collection through its own `utils.refetch()` rather than
 * `queryClient.invalidateQueries`, because the collection is created with its
 * own QueryClient (not necessarily the one in React context), so invalidating
 * the context client wouldn't refresh it.
 *
 * Unlike support-v1, support-v2 tags must belong to a tag group, so
 * `tagGroupId` is required. The picker prevents duplicate names within a group
 * client-side, so a server `Conflict` only happens on a rare race — surfaced as
 * a toast.
 */
export function useCreateSupportTicketTag() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const collections = useGlobalSupportTicketCollections();

  return useMutation<TagResponse, Error, CreateSupportTicketTagInput>({
    mutationFn: async ({ tagGroupId, name, pillTheme }) => {
      const client = effectApi.getClient();
      const { tag } = await effectApi.runPromise(
        catchApiErrors(
          client.tag
            .createTag({ payload: { tagGroupId, name, pillTheme } })
            .pipe(
              Effect.catchTag("Conflict", () => {
                toast(`A tag named "${name}" already exists in this group`, {
                  variant: "error",
                });
                return Effect.die(new Error("Tag name conflict"));
              }),
            ),
        ),
      );
      return tag;
    },
    onSuccess: () => {
      toast("Tag created", { variant: "success" });
      void collections.tags.utils.refetch();
    },
  });
}
